import React, { useState, useEffect, useCallback, useRef } from 'react';
import './AxisScadaOverrides.css';
import {
  Box,
  Typography,
  Paper,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Chip
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Play as PlayIcon,
  Stop as StopIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon
} from '@mui/icons-material';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import pageService from './services/pageService';
import widgetService from './services/widgetService';
import { renderWidget, loadCustomWidgets, getCustomWidgetDefinition } from './widgets';
import TagInfoModal from './widgets/TagInfoModal';
import axiosInstance from '../../services/axiosConfig';
import rmmsLogo from '../../Assets/IMG-20230407-WA0001_transparent.png';
// import * as signalR from '@microsoft/signalr'; // SignalR not needed in RMMS

const PreviewMode = () => {
  const location = useLocation();
  const history = useHistory();
  const { id } = useParams(); // Get page ID from URL params (/axisPreview/:id)
  const [selectedPageId, setSelectedPageId] = useState(
    id ? Number(id) : (location.state?.pageId || null)
  );
  const [homepageId, setHomepageId] = useState(null);

  // Load homepage ID for back navigation
  useEffect(() => {
    const loadHomepage = async () => {
      const homepage = await pageService.getHomePage();
      if (homepage) {
        setHomepageId(homepage.pageId);
      }
    };
    loadHomepage();
  }, []);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isRealTimeMode, setIsRealTimeMode] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState('default');
  const [zoom, setZoom] = useState(100);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Tag info modal state
  const [tagInfoModal, setTagInfoModal] = useState({ open: false, tagInfo: null });

  // Get user's own companyId from sessionStorage (set at login)
  const userCompanyId = sessionStorage.getItem("user_companyId");

  // Navigate to tag edit page (only for companyId=2 users)
  const handleEditTag = useCallback((tagId) => {
    setTagInfoModal({ open: false, tagInfo: null });
    history.push(`/datasources?tab=tags&editTagId=${tagId}`);
  }, [history]);

  const [pages, setPages] = useState([]);

  // Helper function to extract all tagIds from widget including custom widget components
  const getAllWidgetTagIds = useCallback((widget) => {
    const tagIds = [];

    // Helper to add a tag ID (handles various formats)
    const addTagId = (id) => {
      if (!id || id === 'null') return;
      if (typeof id === 'object' && id !== null && id.id) {
        tagIds.push(id.id);
      } else {
        tagIds.push(String(id));
      }
    };

    // Collect all numbered tagIds (tagId, tagId2, ... tagId16) from all sources
    const sources = [widget, widget.properties, widget.elementDetails, widget.props];
    sources.forEach(source => {
      if (!source) return;
      for (let i = 1; i <= 16; i++) {
        const key = i === 1 ? 'tagId' : `tagId${i}`;
        if (source[key]) addTagId(source[key]);
      }
      // Collect special tag IDs
      if (source.onOffStateTagId) addTagId(source.onOffStateTagId);
      if (source.varId) addTagId(source.varId);
    });

    // Handle tagBinding format
    if (widget.properties?.tagBinding?.tagId) {
      addTagId(widget.properties.tagBinding.tagId);
    }

    // Handle custom widgets - extract component tagIds and input-bound tagIds
    const componentType = widget.type || widget.component;
    if (componentType && componentType.startsWith('custom-')) {
      // Extract tag IDs from input_* properties (user-bound tags)
      if (widget.properties) {
        Object.keys(widget.properties).forEach(key => {
          if (key.startsWith('input_')) {
            const val = widget.properties[key];
            if (val && typeof val === 'object' && val.type === 'tag' && val.id) {
              addTagId(val.id);
            } else if (val && typeof val !== 'object') {
              addTagId(val);
            }
          }
        });
      }

      try {
        const customDefinition = getCustomWidgetDefinition(componentType);

        if (customDefinition && customDefinition.widgetConfig) {
          const config = JSON.parse(customDefinition.widgetConfig);
          if (config.components) {
            // Extract tagIds from all components
            config.components.forEach(component => {
              const compTagId = component.tagId || component.properties?.tagId || component.props?.tagId;
              if (compTagId) {
                tagIds.push(compTagId);
              }
            });
          }
        }
      } catch (error) {
        console.error(`Error processing custom widget ${componentType}:`, error);
      }
    }

    return tagIds;
  }, []);

  // Helper function to extract tagId from widget (matches logic in widgets/index.js)
  const getWidgetTagId = useCallback((widget) => {
    let tagId = widget.tagId || widget.properties?.tagId || widget.props?.tagId;

    // Handle tagBinding format (from converter)
    if (!tagId && widget.properties?.tagBinding?.tagId) {
      tagId = widget.properties.tagBinding.tagId;
    }

    // Handle new {type: 'tag', id: 110} format
    if (typeof tagId === 'object' && tagId !== null && tagId.id) {
      tagId = tagId.id;
    }

    return tagId;
  }, []);

  // Mock themes
  const [themes] = useState([
    { id: 'default', name: 'Default Theme' },
    { id: 'dark', name: 'Dark Theme' },
    { id: 'high-contrast', name: 'High Contrast' }
  ]);

  const [widgets, setWidgets] = useState([]);
  const [containers, setContainers] = useState([]);
  const [pageData, setPageData] = useState(null);
  const [tagValues, setTagValues] = useState({});
  const [variables, setVariables] = useState({});

  // Helper function to check if a widget is inside a container
  const isWidgetInContainer = useCallback((widget, container) => {
    return (
      widget.x >= container.x &&
      widget.y >= container.y &&
      widget.x + widget.width <= container.x + container.width &&
      widget.y + widget.height <= container.y + container.height
    );
  }, []);

  // Helper function to find which container a widget belongs to
  const getWidgetContainer = useCallback((widget) => {
    return containers.find(c => isWidgetInContainer(widget, c));
  }, [containers, isWidgetInContainer]);

  // Group widgets by container
  const widgetsByContainer = useCallback(() => {
    const groups = {};
    const orphans = [];

    widgets.forEach(widget => {
      const container = getWidgetContainer(widget);
      if (container) {
        if (!groups[container.id]) {
          groups[container.id] = [];
        }
        groups[container.id].push(widget);
      } else {
        orphans.push(widget);
      }
    });

    return { groups, orphans };
  }, [widgets, getWidgetContainer]);
  const [updateInterval, setUpdateInterval] = useState(null);
  // const [signalRConnection, setSignalRConnection] = useState(null); // SignalR not needed
  const signalRConnection = null; // Placeholder
  const isRealTimeModeRef = useRef(false);
  const [customWidgetsLoaded, setCustomWidgetsLoaded] = useState(false);

  // Load pages
  useEffect(() => {
    const load = async () => {
      // Load custom widgets first to ensure they're available for rendering
      await loadCustomWidgets();

      let list = await pageService.list();
      let normalized = list.map(p => ({ id: p.id, name: p.pageName || p.name }));

      setPages(normalized);

      // Priority: URL param (:id) > query string (?id=) > location.state
      let pid = id ? Number(id) : null;
      if (!pid) {
        const sp = new URLSearchParams(location.search || '');
        const pidFromQs = sp.get('id') || sp.get('pageId') || sp.get('pageid');
        pid = pidFromQs ? Number(pidFromQs) : location.state?.pageId;
      }

      // Default to first page if no specific page requested
      if (!pid) {
        pid = normalized[0]?.id || null;
      }

      setSelectedPageId(pid || null);
      if (pid) {
        const page = await pageService.get(pid);
        const widgetsToSet = Array.isArray(page?.widgets) ? page.widgets : [];

        setPageData(page);
        setWidgets(widgetsToSet);

        // Load containers if available
        if (page?.pageConfig) {
          try {
            const cfg = JSON.parse(page.pageConfig);
            if (cfg.containers && Array.isArray(cfg.containers)) {
              setContainers(cfg.containers);
            }
          } catch (e) {
            // Silent error - container parsing failed
          }
        }
      }
    };
    load();
  }, [id, location.state, location.search]);

  // Load custom widgets immediately on component mount
  useEffect(() => {
    const loadCustomWidgetsForPreview = async () => {
      try {
        await loadCustomWidgets(); // This will populate the cache
        setCustomWidgetsLoaded(true);
      } catch (error) {
        setCustomWidgetsLoaded(true); // Set to true even on error to allow rendering
      }
    };
    loadCustomWidgetsForPreview();
  }, []); // Run only once on mount

  // Listen for page selection events from navigation buttons
  useEffect(() => {
    const handlePageSelect = (event) => {
      const pageId = event.detail?.pageId;
      if (pageId && !isNaN(pageId)) {
        // Update URL so browser back button works
        history.push(`/axisPreview/${pageId}`);
      }
    };

    window.addEventListener('scada-page-select', handlePageSelect);
    return () => {
      window.removeEventListener('scada-page-select', handlePageSelect);
    };
  }, [history]);

  // Sync selectedPageId with URL param when it changes (for back button support)
  useEffect(() => {
    if (id) {
      setSelectedPageId(Number(id));
    }
  }, [id]);

  // When changing page, fetch widgets
  useEffect(() => {
    const loadWidgets = async () => {
      if (!selectedPageId) {
        return;
      }
      const page = await pageService.get(selectedPageId);
      const newWidgets = Array.isArray(page?.widgets) ? page.widgets : [];
      setPageData(page);
      setWidgets(newWidgets);
    };
    loadWidgets();
  }, [selectedPageId]);

  // Container ref for measuring available space
  const containerRef = useRef(null);

  // Scaled dimensions state (DynPages pattern - independent X/Y scaling)
  const [scaledDimensions, setScaledDimensions] = useState({ width: 1920, height: 1080, scaleX: 1, scaleY: 1 });

  // Calculate scaled dimensions based on container size (DynPages pattern)
  const calculateScaledDimensions = useCallback((retryCount = 0) => {
    if (!pageData?.width || !pageData?.height) {
      return;
    }

    if (!containerRef.current) {
      // Retry up to 10 times if container not ready
      if (retryCount < 10) {
        setTimeout(() => calculateScaledDimensions(retryCount + 1), 100);
      }
      return;
    }

    // Get actual container dimensions (clientWidth/clientHeight excludes padding)
    const containerWidth = containerRef.current.clientWidth || containerRef.current.offsetWidth;
    const containerHeight = containerRef.current.clientHeight || containerRef.current.offsetHeight;

    if (containerWidth === 0 || containerHeight === 0) {
      // Retry up to 10 times if container has zero dimensions
      if (retryCount < 10) {
        setTimeout(() => calculateScaledDimensions(retryCount + 1), 100);
      }
      return;
    }

    // Use full container size for scaling; avoid artificial offsets
    const availableWidth = containerWidth;
    const availableHeight = containerHeight;

    // Calculate separate scales for width and height (no aspect ratio preservation)
    // Allow scaling both up and down for responsive behavior like DynPage
    const scaleX = availableWidth / pageData.width;
    const scaleY = availableHeight / pageData.height;

    const scaledWidth = pageData.width * scaleX;
    const scaledHeight = pageData.height * scaleY;

    setScaledDimensions({
      width: scaledWidth,
      height: scaledHeight,
      scaleX: scaleX,
      scaleY: scaleY
    });
  }, [pageData]);

  // Auto-calculate dimensions when pageData changes
  useEffect(() => {
    if (!pageData?.width || !pageData?.height) return;

    // Start with retryCount=0
    const timer = setTimeout(() => calculateScaledDimensions(0), 50);
    return () => clearTimeout(timer);
  }, [pageData, calculateScaledDimensions]);

  // Watch for window resize with debounce (DynPages pattern)
  useEffect(() => {
    let resizeTimer;

    const handleResize = () => {
      // Clear previous timer
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }

      // Debounce: wait 200ms after last resize event
      resizeTimer = setTimeout(() => {
        calculateScaledDimensions(0);
      }, 200);
    };

    window.addEventListener('resize', handleResize);

    // Also listen to visualViewport for browser zoom changes
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
    };
  }, [calculateScaledDimensions]);

  // Watch container size with ResizeObserver
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    let resizeTimer;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Clear previous timer
        if (resizeTimer) {
          clearTimeout(resizeTimer);
        }

        // Debounce
        resizeTimer = setTimeout(() => {
          calculateScaledDimensions(0);
        }, 200);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
    };
  }, [calculateScaledDimensions]);

  // Fetch initial tag values when widgets change
  useEffect(() => {
    const fetchInitialTagValues = async () => {
      if (widgets.length === 0) return;

      try {
        const tagIds = new Set();
        const varIds = new Set();

        widgets.forEach(widget => {
          const widgetTagIds = getAllWidgetTagIds(widget);
          widgetTagIds.forEach(tagId => {
            if (typeof tagId === 'number' || (typeof tagId === 'string' && /^\d+$/.test(tagId))) {
              tagIds.add(tagId);
            } else if (typeof tagId === 'string' && tagId.startsWith('var_')) {
              varIds.add(tagId);
            }
          });
        });

        const allValues = {};

        // Fetch Tag Values
        if (tagIds.size > 0) {
          const tagObjects = Array.from(tagIds).map(tagId => ({
            TagId: tagId.toString()
          }));

          const tagResponse = await axiosInstance.post(
            `https://app-rmmsapi.azurewebsites.net/api/Tags/GetTagValues`,
            tagObjects
          );

          if (tagResponse.data && Array.isArray(tagResponse.data)) {
            tagResponse.data.forEach((tag) => {
              // Handle both TagValue and tagValue property names
              const tagValue = tag.TagValue || tag.tagValue;
              const parts = tagValue ? tagValue.toString().trim().split(/\s+/) : [];
              const numericValue = parts.length > 0 ? parseFloat(parts[0]) : 0;
              const unit = parts.length > 1 ? parts.slice(1).join(' ') : '';
              const tagId = String(tag.TagId || tag.tagId); // Ensure string key
              const companyId = tag.CompanyId || tag.companyId;
              const tagName = tag.TagName || tag.tagName || '';
              allValues[tagId] = {
                value: numericValue,
                companyId: companyId,
                raw: tagValue,
                unit: unit,
                name: tagName
              };
            });
          } else {
            console.error('❌ Tag response data is not an array:', tagResponse.data);
          }
        }

        // Fetch Variable Values
        if (varIds.size > 0) {
          const varObjects = Array.from(varIds).map(varId => ({
            varId: varId.replace('var_', '') // Remove 'var_' prefix to get actual ID
          }));

          try {
            const varResponse = await axiosInstance.post(
              `https://app-rmmsapi.azurewebsites.net/api/Tags/GetVarValues`,
              varObjects
            );

            if (varResponse.data && Array.isArray(varResponse.data)) {
              varResponse.data.forEach((varItem) => {
                // API returns Value and Unit as separate fields
                const rawValue = varItem.value || varItem.Value || '';
                const unit = varItem.unit || varItem.Unit || '';
                const numericValue = parseFloat(rawValue) || 0;
                const varKey = `var_${varItem.varId}`;
                // Store raw numeric value - formatting will be done by widget based on its properties
                allValues[varKey] = {
                  raw: numericValue,
                  unit: unit,
                  formatted: rawValue, // Keep raw for widget to format
                  display: rawValue    // Keep raw for widget to format
                };
              });
            }
          } catch (varError) {
            console.error('Error fetching variable values:', varError);
          }
        }

        setTagValues(allValues);
      } catch (error) {
        console.error('Error fetching initial tag/variable values:', error);
      }
    };

    fetchInitialTagValues();
  }, [widgets]);

  // Setup automatic updates for normal mode (always running in background)
  useEffect(() => {
    if (widgets.length === 0) return;

    const refreshTagValues = async () => {
      try {
        const tagIds = new Set();
        const varIds = new Set();

        widgets.forEach(widget => {
          const widgetTagIds = getAllWidgetTagIds(widget);
          widgetTagIds.forEach(tagId => {
            if (typeof tagId === 'number' || (typeof tagId === 'string' && /^\d+$/.test(tagId))) {
              tagIds.add(tagId);
            } else if (typeof tagId === 'string' && tagId.startsWith('var_')) {
              varIds.add(tagId);
            }
          });
        });

        const allValues = {};

        // Fetch Tag Values
        if (tagIds.size > 0) {
          const tagObjects = Array.from(tagIds).map(tagId => ({
            TagId: tagId.toString()
          }));

          const tagResponse = await axiosInstance.post(
            `https://app-rmmsapi.azurewebsites.net/api/Tags/GetTagValues`,
            tagObjects
          );

          if (tagResponse.data && Array.isArray(tagResponse.data)) {
            tagResponse.data.forEach(tag => {
              const tagValue = tag.TagValue || tag.tagValue;
              const parts = tagValue ? tagValue.toString().trim().split(/\s+/) : [];
              const numericValue = parts.length > 0 ? parseFloat(parts[0]) : 0;
              const unit = parts.length > 1 ? parts.slice(1).join(' ') : '';
              const tagId = String(tag.TagId || tag.tagId);
              const companyId = tag.CompanyId || tag.companyId;
              const tagName = tag.TagName || tag.tagName || '';
              allValues[tagId] = {
                value: numericValue,
                companyId: companyId,
                raw: tagValue,
                unit: unit,
                name: tagName
              };
            });
          }
        }

        // Fetch Variable Values
        if (varIds.size > 0) {
          const varObjects = Array.from(varIds).map(varId => ({
            varId: varId.replace('var_', '')
          }));

          try {
            const varResponse = await axiosInstance.post(
              `https://app-rmmsapi.azurewebsites.net/api/Tags/GetVarValues`,
              varObjects
            );

            if (varResponse.data && Array.isArray(varResponse.data)) {
              varResponse.data.forEach((varItem) => {
                // API returns Value and Unit as separate fields
                const rawValue = varItem.value || varItem.Value || '';
                const unit = varItem.unit || varItem.Unit || '';
                const numericValue = parseFloat(rawValue) || 0;
                const varKey = `var_${varItem.varId}`;
                // Store raw numeric value - formatting will be done by widget based on its properties
                allValues[varKey] = {
                  raw: numericValue,
                  unit: unit,
                  formatted: rawValue, // Keep raw for widget to format
                  display: rawValue    // Keep raw for widget to format
                };
              });
            }
          } catch (varError) {
            console.error('Error fetching variable values:', varError);
          }
        }

        setTagValues(allValues);
      } catch (error) {
        console.error('Error refreshing tag values:', error);
      }
    };

    // Start timer-based updates every 10 seconds (only in normal mode)
    let interval = null;
    if (!isRealTimeMode) {
      interval = setInterval(refreshTagValues, 10000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [widgets, isRealTimeMode]);

  const handleFullscreenToggle = useCallback(() => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, [isFullscreen]);


  // Setup SignalR connection - DISABLED for RMMS
  const setupSignalRConnection = useCallback(async () => {
    // SignalR not needed in RMMS
    return null;
  }, []);

  // Initialize SignalR connection when component mounts - DISABLED
  useEffect(() => {
    isRealTimeModeRef.current = isRealTimeMode;
    // setupSignalRConnection(); // Disabled
  }, [setupSignalRConnection]);

  // Update ref when real-time mode changes
  useEffect(() => {
    isRealTimeModeRef.current = isRealTimeMode;
  }, [isRealTimeMode]);

  const handleRealTimeToggle = useCallback(() => {
    setIsRealTimeMode(prev => {
      const newMode = !prev;
      isRealTimeModeRef.current = newMode;
      return newMode;
    });
  }, []);

  // Execute workflow when widget action is triggered
  const executeWorkflow = useCallback(async (workflowName, inputBindings) => {
    try {
      // Prepare input values from bindings
      const inputs = {};
      if (inputBindings) {
        Object.entries(inputBindings).forEach(([paramName, binding]) => {
          if (binding.source === 'tag') {
            inputs[paramName] = tagValues[binding.path] || 0;
          } else if (binding.source === 'variable') {
            inputs[paramName] = variables[binding.path] || 0;
          } else if (binding.source === 'widget') {
            // Parse widget path (e.g., "textbox_1.value")
            const [widgetId, property] = binding.path.split('.');
            const widget = widgets.find(w => w.id === widgetId);
            inputs[paramName] = widget?.properties?.[property] || 0;
          }
        });
      }
      
      // Execute workflow via API
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5169/api'}/Task/execute-workflow`,
        {
          workflowName,
          companyId: 1,
          inputs
        }
      );
      
      // Update variables with workflow results
      if (response.data?.variables) {
        setVariables(prev => ({ ...prev, ...response.data.variables }));
      }
      
      return response.data;
    } catch (error) {
      console.error('Error executing workflow:', error);
      return null;
    }
  }, [tagValues, variables, widgets]);

  const handleRefresh = useCallback(async () => {
    try {
      const tagIds = new Set();
      widgets.forEach(widget => {
        const widgetTagIds = getAllWidgetTagIds(widget);
        widgetTagIds.forEach(tagId => {
          tagIds.add(tagId);
        });
      });

      if (tagIds.size > 0) {
        const tagObjects = Array.from(tagIds).map(tagId => ({
          TagId: tagId
        }));

        const response = await axiosInstance.post(
          `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5169/api'}/Tags/GetTagValues`,
          tagObjects
        );

        if (response.data && Array.isArray(response.data)) {
          const newTagValues = {};
          response.data.forEach(tag => {
            const tagValue = tag.TagValue || tag.tagValue;
            const parts = tagValue ? tagValue.toString().trim().split(/\s+/) : [];
            const numericValue = parts.length > 0 ? parseFloat(parts[0]) : 0;
            const unit = parts.length > 1 ? parts.slice(1).join(' ') : '';
            const tagId = String(tag.TagId || tag.tagId);
            const companyId = tag.CompanyId || tag.companyId;
            const tagName = tag.TagName || tag.tagName || '';
            newTagValues[tagId] = {
              value: numericValue,
              companyId: companyId,
              raw: tagValue,
              unit: unit,
              name: tagName
            };
          });
          setTagValues(newTagValues);
        }
      }
    } catch (error) {
      console.error('Error refreshing tag values:', error);
    }
  }, [widgets]);

  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + 10, 200));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev - 10, 50));
  }, []);


  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (updateInterval) {
        clearInterval(updateInterval);
      }
      if (signalRConnection) {
        signalRConnection.stop();
      }
    };
  }, [updateInterval, signalRConnection]);

  const selectedPage = pages.find(p => p.id === selectedPageId);

  return (
    // Use fixed height calculation to prevent white bar at bottom
    // Header is 70px, so available height is 100vh - 70px
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: 'calc(100vh - 70px)', // Fixed height accounting for header
      overflow: 'hidden'
    }}>
      {/* Preview Canvas */}
      <Box ref={containerRef} sx={{
        flex: 1,
        position: 'relative',
        overflow: 'hidden',
        bgcolor: pageData?.backgroundColor || (selectedTheme === 'dark' ? 'grey.900' : 'white'),
        padding: 0,
        margin: 0,
        minHeight: 0
      }}>
        {selectedPage ? (
          <Box
            sx={{
              position: 'relative',
              width: `${scaledDimensions.width}px`,
              height: `${scaledDimensions.height}px`,
              bgcolor: pageData?.backgroundColor || (selectedTheme === 'dark' ? 'grey.900' : 'white'),
              margin: 0,
              padding: 0
            }}
          >
            {/* Background Image Layer */}
            {pageData?.backgroundImage && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${pageData.backgroundImage === '/rmmslogo.png' ? rmmsLogo : pageData.backgroundImage})`,
                  backgroundSize: pageData.backgroundSize || 'contain',
                  backgroundPosition: pageData.backgroundPosition || 'center',
                  backgroundRepeat: pageData.backgroundRepeat || 'no-repeat',
                  opacity: pageData.backgroundOpacity || 0.3,
                  maskImage: 'radial-gradient(ellipse at center, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.15) 70%, rgba(0,0,0,0) 100%)',
                  WebkitMaskImage: 'radial-gradient(ellipse at center, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.15) 70%, rgba(0,0,0,0) 100%)',
                  pointerEvents: 'none',
                  zIndex: 0
                }}
              />
            )}
            <Box sx={{ position: 'relative', zIndex: 1, width: '100%', height: '100%' }}>

            {/* Render Widgets with Container Hierarchy - Wait for custom widgets to load */}
            {!customWidgetsLoaded ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <Typography variant="body2" color="text.secondary">Loading custom widgets...</Typography>
              </Box>
            ) : (() => {
              // Helper function to render a single widget
              const renderSingleWidget = (widget, containerX = 0, containerY = 0) => {
                // Extract tagId (handle both old format and new {type, id} format)
                // Check multiple possible locations for tagId
                let widgetTagId = widget.properties?.tagId || widget.props?.tagId || widget.tagId;

                // Handle new {type: 'var'|'tag', id: 'var_6'|110} format - prefer this over tagBinding
                if (typeof widgetTagId === 'object' && widgetTagId !== null && widgetTagId.id) {
                  // Use the full id which includes 'var_' prefix for variables
                  widgetTagId = widgetTagId.id;
                } else if (widget.properties?.tagBinding?.tagId) {
                  // Fallback to tagBinding format - check if tagId object exists to determine type
                  const tagIdObj = widget.properties?.tagId;
                  if (typeof tagIdObj === 'object' && tagIdObj !== null && tagIdObj.type === 'var') {
                    // It's a variable, use the var_ prefixed id from the object
                    widgetTagId = tagIdObj.id;
                  } else if (!widgetTagId) {
                    // It's a regular tag, use tagBinding value
                    widgetTagId = widget.properties.tagBinding.tagId;
                  }
                }

                // Normalize tagId to string for consistent lookup
                const normalizedTagId = widgetTagId ? String(widgetTagId) : null;
                const rawTagValue = normalizedTagId ? tagValues[normalizedTagId] : undefined;

                // Handle object formats:
                // - Var values: {raw, unit, formatted, display}
                // - Tag values: {value, companyId, raw}
                const isVarValue = rawTagValue && typeof rawTagValue === 'object' && rawTagValue.display !== undefined;
                const isTagValueObject = rawTagValue && typeof rawTagValue === 'object' && rawTagValue.value !== undefined && rawTagValue.companyId !== undefined;

                let tagValueRaw;
                let tagCompanyId = null;
                if (isVarValue) {
                  tagValueRaw = rawTagValue.raw;
                } else if (isTagValueObject) {
                  tagValueRaw = rawTagValue.value;
                  tagCompanyId = rawTagValue.companyId;
                } else {
                  tagValueRaw = rawTagValue;
                }

                // Apply divideBy factor only if explicitly specified in widget properties
                const divideBy = widget.properties?.divideBy ?? widget.elementDetails?.divideBy ?? 1;
                if (divideBy !== 1 && typeof tagValueRaw === 'number') {
                  tagValueRaw = tagValueRaw / divideBy;
                } else if (divideBy !== 1 && typeof tagValueRaw === 'string' && !isNaN(parseFloat(tagValueRaw))) {
                  tagValueRaw = parseFloat(tagValueRaw) / divideBy;
                }

                // Format value based on widget's decimal property and separators
                const decimalPlaces = widget.properties?.decimal ?? widget.elementDetails?.decimal ?? 1;
                const decimalSeparator = widget.properties?.decimalSeparator ?? widget.elementDetails?.decimalSeparator ?? ',';
                const thousandSeparator = widget.properties?.thousandSeparator ?? widget.elementDetails?.thousandSeparator ?? '.';

                let tagValue;
                if (isVarValue) {
                  // Format number with widget's separator settings
                  let formattedNum = '0';
                  if (Number.isFinite(tagValueRaw)) {
                    // Format with decimal places
                    const fixedNum = tagValueRaw.toFixed(decimalPlaces);
                    const [intPart, decPart] = fixedNum.split('.');

                    // Add thousand separators to integer part
                    const intWithThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);

                    // Combine with decimal separator
                    formattedNum = decPart ? `${intWithThousands}${decimalSeparator}${decPart}` : intWithThousands;
                  }
                  const showUnit = widget.properties?.showUnit !== false;
                  const unit = showUnit ? (rawTagValue.unit || '') : '';
                  tagValue = unit ? `${formattedNum} ${unit}` : formattedNum;
                } else {
                  tagValue = tagValueRaw;
                }

                // Scale widget position and size independently (DynPages pattern)
                const scaleX = scaledDimensions.scaleX || 1;
                const scaleY = scaledDimensions.scaleY || 1;

                // Handler for right-click to show tag info
                const handleContextMenu = (e) => {
                  // Check if widget has a tag binding
                  const hasTag = normalizedTagId || widget.properties?.tagName;

                  if (hasTag) {
                    e.preventDefault();
                    e.stopPropagation();

                    // Get tag name from tagValues or widget properties
                    const tagName = (rawTagValue && typeof rawTagValue === 'object' && rawTagValue.name) || widget.properties?.tagName || `Tag_${normalizedTagId}`;

                    // Dispatch openTagInfo event for TagInfoModalManager (Turkish modal with trend buttons)
                    window.dispatchEvent(new CustomEvent('openTagInfo', {
                      detail: {
                        tagId: normalizedTagId,
                        tagName: tagName,
                        currentValue: tagValue
                      }
                    }));
                  }
                };

                return (
                  <Box
                    key={`${widget.id}-${tagValueRaw}`}
                    onContextMenu={handleContextMenu}
                    sx={{
                      position: 'absolute',
                      left: (widget.x - containerX) * scaleX,
                      top: (widget.y - containerY) * scaleY,
                      width: widget.width,
                      height: widget.height,
                      transform: `scale(${scaleX}, ${scaleY})`,
                      transformOrigin: 'top left',
                      overflow: 'visible',
                      zIndex: widget.zIndex || 0,
                      pointerEvents: 'auto'
                    }}
                  >
                    {/* Render actual widget component - Enhanced for Custom Widgets */}
                    {(() => {
                      // Determine the property name for tag value based on tagBinding
                      const tagPropName = widget.properties?.tagBinding?.property || 'tagValue';

                      // Normalize borderWidth to counteract CSS transform scale
                      // Without this, borders appear thicker when widget is scaled up
                      const rawBorderWidth = widget.properties?.borderWidth ?? widget.borderWidth;
                      const avgScale = (scaleX + scaleY) / 2;
                      const normalizedBorderWidth = (rawBorderWidth && avgScale > 0)
                        ? rawBorderWidth / avgScale
                        : rawBorderWidth;

                      const widgetProps = {
                        ...widget,
                        ...widget.elementDetails, // Include elementDetails (contains numberFormat, etc.)
                        ...widget.properties, // Include all properties - this spreads borderRadius etc.
                        tagId: normalizedTagId,
                        [tagPropName]: tagValue, // Use dynamic prop name from tagBinding
                        companyId: tagCompanyId, // Pass tag's companyId for ButtonMenu commands
                        onContextMenu: handleContextMenu, // Pass context menu handler to widgets
                        ...(normalizedBorderWidth !== undefined && { borderWidth: normalizedBorderWidth })
                      };

                      // DEBUG: Removed excessive logging

                      // Enhanced custom widget support with input mapping
                      const componentType = widget.type || widget.component;

                      if (componentType && componentType.startsWith('custom-')) {
                        // Get custom widget definition to understand inputs
                        const customDefinition = getCustomWidgetDefinition(componentType);
                        if (customDefinition && customDefinition.widgetConfig) {
                          try {
                            const config = JSON.parse(customDefinition.widgetConfig);
                            const metadata = config.metadata || {};
                            const inputs = metadata.inputs || [];

                            // Map widget properties to custom widget inputs
                            // For each input defined in the custom widget, try to map from widget properties
                            inputs.forEach(input => {
                              // Try multiple possible property names for the input
                              const possibleInputValues = [
                                widget.properties?.[input.name], // Direct property name
                                widget[input.name], // Widget level property
                                widget.properties?.[`input_${input.name}`], // Prefixed property
                                widget.properties?.[input.name.toLowerCase()], // Lowercase
                                widget.properties?.[input.name.toUpperCase()], // Uppercase
                              ];

                              // Use the first non-undefined value
                              const inputValue = possibleInputValues.find(val => val !== undefined);

                              if (inputValue !== undefined) {
                                // If the input value is a tag ID, get the tag value
                                let resolvedValue = inputValue;

                                // Handle new {type: 'tag', id: 110} format
                                if (typeof inputValue === 'object' && inputValue !== null && inputValue.type === 'tag' && inputValue.id) {
                                  resolvedValue = tagValues[inputValue.id];
                                } else if (typeof inputValue === 'string' && tagValues[inputValue] !== undefined) {
                                  resolvedValue = tagValues[inputValue];
                                }

                                // Store the resolved value in properties for custom widget
                                widgetProps.properties = widgetProps.properties || {};
                                widgetProps.properties[input.name] = resolvedValue;
                              }
                            });
                          } catch (error) {
                            console.error('PreviewMode: Error processing custom widget inputs:', error);
                          }
                        }
                      }

                      return renderWidget(widgetProps, tagValues, true, avgScale);
                    })() || (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        color: 'text.secondary',
                        border: '1px dashed #ccc',
                        bgcolor: '#f5f5f5'
                      }}>
                        <Typography variant="caption">
                          {widget.name || widget.type}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              };

              if (containers.length === 0) {
                // No containers - render widgets normally
                return widgets.map((widget) => renderSingleWidget(widget, 0, 0));
              }

              // With containers - create proper container hierarchy
              const { groups, orphans } = widgetsByContainer();
              const scaleX = scaledDimensions.scaleX || 1;
              const scaleY = scaledDimensions.scaleY || 1;

              return (
                <>
                  {/* Render containers with their widgets */}
                  {containers.map((container) => {
                    const containerWidgets = (groups[container.id] || [])
                      .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

                    return (
                      <Box
                        key={container.id}
                        data-container-id={container.id}
                        sx={{
                          position: 'absolute',
                          left: container.x * scaleX,
                          top: container.y * scaleY,
                          width: container.width,
                          height: container.height,
                          zIndex: 0,
                          pointerEvents: 'none',
                          isolation: 'isolate' // Create stacking context
                        }}
                      >
                        {containerWidgets.map((widget) => renderSingleWidget(widget, container.x, container.y))}
                      </Box>
                    );
                  })}

                  {/* Render orphan widgets (not in any container) */}
                  {orphans.map((widget) => renderSingleWidget(widget, 0, 0))}
                </>
              );
            })()}
          </Box>

          </Box>
        ) : null}
      </Box>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)}>
        <DialogTitle>Preview Settings</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Theme</InputLabel>
            <Select
              value={selectedTheme}
              onChange={(e) => setSelectedTheme(e.target.value)}
              label="Theme"
            >
              {themes.map(theme => (
                <MenuItem key={theme.id} value={theme.id}>
                  {theme.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={isRealTimeMode}
                onChange={handleRealTimeToggle}
              />
            }
            label="Enable Real-time Data Updates"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Tag Info Modal */}
      <TagInfoModal
        open={tagInfoModal.open}
        onClose={() => setTagInfoModal({ open: false, tagInfo: null })}
        tagInfo={tagInfoModal.tagInfo}
        userCompanyId={userCompanyId}
        onEditTag={handleEditTag}
      />
    </Box>
  );
};

export default PreviewMode;
